<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    onMounted,
    ref,
    shallowRef,
  } from 'vue';
  import { useOnline } from '@vueuse/core';
  import { MenuImportsService } from '@/api/services/menu-imports.service';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { useMenuImport } from '@/composition/menu-import/menu-import-context';
  import type { TMenuImportEntry } from '@/composition/menu-import/menu-import-context';
  import type { TCaptureOrigin } from '@/composition/menu-import/use-menu-capture';
  import { flowBar } from '@/composition/store-setup/setup-flow-screen';
  import type { IFlowBarState } from '@/composition/store-setup/setup-flow-screen';
  import { useGtag } from '@/composition/use-gtag';
  import { useModal } from '@/composition/use-modal';
  import { readCta, trayTiles } from '@/domain/menu-import/capture-tray';
  import type { ITrayTile } from '@/domain/menu-import/capture-tray';
  import { draftHasItems } from '@/domain/menu-import/menu-draft';
  import { isFirstMenuPath } from '@/domain/store-setup/store-setup-analytics';
  import type { ICopySource, TSetupMenuPath } from '@/interfaces/IStoreSetup';
  import { nextDomId } from '@/utils/dom-id.utils';
  import V2MenuCaptureTray from './V2MenuCaptureTray.vue';
  import V2MenuCopyPickerModal from './V2MenuCopyPickerModal.vue';
  import V2MenuPasteCard from './V2MenuPasteCard.vue';
  import V2MenuSourceCards from './V2MenuSourceCards.vue';

  /**
   * «Ahora, su menú» (plan §4.3): the ways in (photos, a PDF or spreadsheet,
   * typed or pasted text, a copy of another own store, or «Cargarlo a mano»
   * when the AI is off), the tray of pages with the note, and «Leer mi carta»
   * in the bottom bar. Owns the note, the paste card and the copy sources;
   * everything else comes from the menu import context.
   */
  const NOTE_MAX = 300;

  const props = withDefaults(
    defineProps<{
      entry: TMenuImportEntry;
      /** The draft already has dishes: this screen adds pages to it. */
      again: boolean;
      phone: boolean;
      restaurantId: number;
      /** Opens with the paste card («Escribirlo o pegarlo» from a failed read). */
      initialPaste?: boolean;
    }>(),
    { initialPaste: false }
  );
  const emit = defineEmits<{ (e: 'skip'): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const trc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));
  const ctx = useMenuImport();
  const { capture } = ctx;
  const modals = useModal();
  const gtag = useGtag();
  const online = useOnline();
  const noteId = nextDomId('menu-note');

  const note = ref('');
  const pasteOpen = ref(props.initialPaste && ctx.aiEnabled.value);
  const pasteBusy = ref(false);
  /** Why the pasted text could not be sent (an i18n key), shown in the card. */
  const pasteError = ref<string | null>(null);
  /** `POST …/reads` is running. */
  const starting = ref(false);
  /** A copy or the manual start is running. */
  const acting = ref(false);
  /** Pages being removed (a very tall page is deleted part by part): no read meanwhile. */
  const removing = ref(0);
  const copySources = shallowRef<ICopySource[]>([]);
  let pathReported = false;
  let alive = true;

  const aiEnabled = computed(() => ctx.aiEnabled.value);
  const tiles = computed(() =>
    trayTiles(capture.entries.value, ctx.pages.value)
  );
  /** «Subiendo {count}…» counts pages as the tray shows them, not the parts of a long one. */
  const pendingPages = computed(
    () =>
      tiles.value.filter(
        (tile) => tile.state === 'processing' || tile.state === 'uploading'
      ).length
  );
  const cta = computed(() =>
    readCta(ctx.pages.value, pendingPages.value, capture.busy.value)
  );
  const cardsDisabled = computed(
    () => starting.value || acting.value || pasteBusy.value
  );
  /** Links that call the server: also off while offline (§4.0). */
  const linksDisabled = computed(() => cardsDisabled.value || !online.value);
  /** Photos wait for the network in the tray; «Cargarlo a mano» (AI off) needs it at once. */
  const sourceCardsDisabled = computed(() =>
    aiEnabled.value ? cardsDisabled.value : linksDisabled.value
  );
  const copyChoices = computed(() =>
    draftHasItems(ctx.draft.value) ? [] : copySources.value
  );
  const copyLabel = computed(() =>
    copyChoices.value.length === 1
      ? tr('menuImport.source.copyFrom', { store: copyChoices.value[0].name })
      : tr('menuImport.source.copyFromOther')
  );
  const showTypeLink = computed(
    () => props.phone && aiEnabled.value && !pasteOpen.value
  );
  const showSkip = computed(() => props.entry === 'setup' && !props.again);
  const hasOtherWays = computed(
    () => showTypeLink.value || copyChoices.value.length > 0 || showSkip.value
  );
  /** Before the first page only: then the tray takes their place. */
  const showTips = computed(
    () => aiEnabled.value && !pasteOpen.value && tiles.value.length === 0
  );
  const showTray = computed(() => aiEnabled.value && tiles.value.length > 0);
  /** «Actualizar desde fotos»: a store with dishes updates its menu (plan §3.3). */
  const updating = computed(
    () =>
      props.entry === 'import' && !props.again && ctx.existingItems.value > 0
  );
  const title = computed(() => {
    if (props.again) return tr('menuImport.source.againTitle');
    if (updating.value) return tr('menuImport.update.sourceTitle');
    return props.entry === 'import'
      ? tr('menuImport.source.titleImport')
      : tr('menuImport.source.title');
  });
  /** Both subtitles promise a reading: none while the AI is off (its notice says what to do). */
  const subtitle = computed(() => {
    if (!aiEnabled.value) return null;
    if (props.again) return tr('menuImport.source.againText');
    return updating.value
      ? tr('menuImport.update.sourceSubtitle')
      : tr('menuImport.source.subtitle');
  });
  /** The AI-off notice offers the copy only when there is a store to copy from. */
  const aiOffText = computed(() =>
    copyChoices.value.length > 0
      ? tr('menuImport.source.aiOff')
      : tr('menuImport.source.aiOffManual')
  );

  //#region Bottom bar
  const bar = computed<IFlowBarState>(() => {
    // Adding pages to a menu being reviewed: «Atrás» goes back to it.
    const backLabel = props.again ? tr('storeSetup.back') : null;
    // The paste card has its own button; with the AI off nothing can be read.
    if (pasteOpen.value || !aiEnabled.value) return flowBar({ backLabel });
    const model = cta.value;
    return flowBar({
      backLabel,
      helper: tr(
        model.long ? 'menuImport.read.helperLong' : 'menuImport.read.helper'
      ),
      primaryLabel: model.plural
        ? trc(model.labelKey, model.count, { count: model.count })
        : tr(model.labelKey, { count: model.count }),
      primaryDisabled:
        !model.ready ||
        !online.value ||
        acting.value ||
        pasteBusy.value ||
        removing.value > 0,
      primaryBusy: starting.value,
    });
  });

  async function primary() {
    const state = bar.value;
    if (state.primaryLabel === null || state.primaryDisabled) return;
    await startRead();
  }

  async function back() {
    if (!props.again) return;
    blurActive();
    ctx.view.value = 'workspace';
  }

  /** The source screen has no secondary action. */
  function nothing(): Promise<void> {
    return Promise.resolve();
  }
  //#endregion

  //#region Actions
  function blurActive() {
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
  }

  /** `menu_path_chosen`, once, on the first way in (§4.13): not again once the session exists. */
  function reportPath(path: TSetupMenuPath) {
    const first = isFirstMenuPath({
      sessionId: ctx.sessionId.value,
      reported: pathReported,
    });
    if (!first || props.again) return;
    pathReported = true;
    gtag.event('menu_path_chosen', { path });
  }

  function addFiles(files: File[], origin: TCaptureOrigin) {
    reportPath(origin === 'camera' ? 'photos' : 'file');
    capture.addFiles(files, origin);
  }

  async function startRead() {
    if (starting.value) return;
    const pages = cta.value.count;
    const hasNote = note.value.trim().length > 0;
    starting.value = true;
    blurActive();
    const started = await ctx.startRead(note.value);
    if (started)
      gtag.event('menu_read_started', {
        pages,
        readNo: ctx.read.value?.readNo ?? null,
        hasNote,
      });
    // A started read opens the workspace, which unmounts this screen.
    if (!alive) return;
    starting.value = false;
  }

  function openPaste() {
    pasteError.value = null;
    pasteOpen.value = true;
  }

  function closePaste() {
    pasteError.value = null;
    pasteOpen.value = false;
  }

  /** «Ordenar mi menú»: the text becomes a page, then it is read with the others. */
  async function submitPaste(text: string) {
    if (pasteBusy.value) return;
    reportPath('text');
    pasteBusy.value = true;
    pasteError.value = null;
    const outcome = await capture.addText(text, 'paste', null);
    if (!alive) return;
    pasteBusy.value = false;
    if (outcome.status === 'failed') {
      // The text stays in the card; sending it again is the same page.
      pasteError.value = outcome.errorKey;
      return;
    }
    // The text is a page of the tray now: sending it again would add it twice.
    pasteOpen.value = false;
    await startRead();
  }

  async function chooseCopySource(): Promise<ICopySource | null> {
    const choices = copyChoices.value;
    if (choices.length === 1) return choices[0];
    const restaurantId = await modals.showDynamicDialog<number | null>(
      V2MenuCopyPickerModal,
      { sources: choices },
      'v2-menu-copy-picker',
      { width: 440 }
    );
    return (
      choices.find((source) => source.restaurantId === restaurantId) ?? null
    );
  }

  async function copyMenu() {
    if (linksDisabled.value) return;
    const source = await chooseCopySource();
    if (!source || !alive) return;
    const confirmed = await modals.confirm({
      title: tr('menuImport.source.copyTitle'),
      message: trc('menuImport.source.copyMessage', source.items, {
        count: source.items,
        store: source.name,
      }),
      acceptText: tr('menuImport.source.copyAccept'),
      cancelText: tr('cancel'),
    });
    if (!confirmed || !alive) return;
    reportPath('copy');
    acting.value = true;
    blurActive();
    await ctx.copyFrom(source.restaurantId);
    // A copy opens the workspace, which unmounts this screen.
    if (!alive) return;
    acting.value = false;
  }

  async function skip() {
    if (linksDisabled.value) return;
    const confirmed = await modals.confirm({
      title: tr('menuImport.source.skipTitle'),
      message: tr('menuImport.source.skipMessage'),
      acceptText: tr('menuImport.source.skipAccept'),
      cancelText: tr('menuImport.source.skipCancel'),
    });
    if (!confirmed || !alive) return;
    reportPath('skip');
    emit('skip');
  }

  /** «Cargarlo a mano» (AI off): an empty workspace. */
  async function startManual() {
    if (linksDisabled.value) return;
    reportPath('manual');
    acting.value = true;
    blurActive();
    await ctx.startManual();
    if (!alive) return;
    acting.value = false;
  }
  //#endregion

  //#region Tray
  /** «Sacar de nuevo» (a whole photo, the only page with a warning): the new photo takes its place. */
  async function retake(tile: ITrayTile, file: File) {
    const [member] = tile.members;
    if (!member) return;
    if (member.localId) {
      await capture.retake(member.localId, file);
      return;
    }
    // A page uploaded before a reload or from another device: remove it, then add the new photo.
    if (!member.pageId || !(await capture.removePage(member.pageId)) || !alive)
      return;
    capture.addFiles([file], props.phone ? 'camera' : 'gallery');
  }

  function useAnyway(tile: ITrayTile) {
    const [member] = tile.members;
    if (member?.localId) capture.useAnyway(member.localId);
  }

  /** «Quitar»: every part of the page, one after the other (a refused removal stops it). */
  async function removeTile(tile: ITrayTile) {
    removing.value++;
    for (const member of tile.members) {
      const removed = member.localId
        ? await capture.remove(member.localId)
        : member.pageId
        ? await capture.removePage(member.pageId)
        : true;
      if (!alive) return;
      if (!removed) break;
    }
    removing.value--;
  }

  function retryTile(tile: ITrayTile) {
    for (const member of tile.members) {
      if (member.localId) capture.retry(member.localId);
    }
  }
  //#endregion

  /** The other own stores to copy from; only offered while the draft has no dishes. */
  async function loadCopySources() {
    if (draftHasItems(ctx.draft.value)) return;
    try {
      const sources = await MenuImportsService.copySources(props.restaurantId);
      if (alive) copySources.value = sources;
    } catch (error) {
      // Copying is optional: without the list the link is simply not offered.
      console.warn('[menu-import] copy sources not loaded', error);
    }
  }

  onMounted(() => {
    void loadCopySources();
  });

  onBeforeUnmount(() => {
    alive = false;
  });

  defineExpose({ bar, primary, secondary: nothing, back });
</script>

<template>
  <div class="v2-menu-source-step">
    <header class="step-head">
      <h1 class="step-title" tabindex="-1">{{ title }}</h1>
      <p v-if="subtitle" class="step-subtitle">{{ subtitle }}</p>
    </header>
    <p v-if="!aiEnabled" class="ai-off" role="status">
      <PosUiIcon name="information-outline" class="notice-icon" />
      <span>{{ aiOffText }}</span>
    </p>
    <V2MenuPasteCard
      v-if="pasteOpen"
      :busy="pasteBusy"
      :disabled="!online"
      :error="pasteError ? $t(pasteError) : null"
      @submit="submitPaste"
      @cancel="closePaste"
    />
    <V2MenuSourceCards
      v-else
      :phone="phone"
      :ai-enabled="aiEnabled"
      :disabled="sourceCardsDisabled"
      @files="addFiles"
      @paste="openPaste"
      @manual="startManual"
    />
    <div v-if="hasOtherWays" class="other-ways">
      <span class="other-label">{{ $t('menuImport.source.otherWays') }}</span>
      <button
        v-if="showTypeLink"
        type="button"
        class="way-link"
        :disabled="cardsDisabled"
        @click="openPaste"
      >
        {{ $t('menuImport.source.typeTitle') }}
      </button>
      <button
        v-if="copyChoices.length > 0"
        type="button"
        class="way-link"
        :disabled="linksDisabled"
        @click="copyMenu"
      >
        {{ copyLabel }}
      </button>
      <button
        v-if="showSkip"
        type="button"
        class="way-link is-quiet"
        :disabled="linksDisabled"
        @click="skip"
      >
        {{ $t('menuImport.source.skip') }}
      </button>
    </div>
    <p v-if="!phone && aiEnabled" class="desktop-tip">
      {{ $t('menuImport.source.desktopPhoneTip') }}
    </p>
    <template v-if="showTips">
      <ul class="tips">
        <li v-if="updating" class="tip is-key">
          <PosUiIcon name="information-outline" class="tip-icon" />
          <span>{{ $t('menuImport.update.sourceTip') }}</span>
        </li>
        <li class="tip">
          <PosUiIcon name="white-balance-sunny" class="tip-icon" />
          <span>{{ $t('menuImport.source.tipLight') }}</span>
        </li>
        <li class="tip">
          <PosUiIcon name="crop-free" class="tip-icon" />
          <span>{{ $t('menuImport.source.tipWhole') }}</span>
        </li>
        <li class="tip">
          <PosUiIcon name="file-multiple-outline" class="tip-icon" />
          <span>{{ $t('menuImport.source.tipOnePage') }}</span>
        </li>
      </ul>
      <p class="ai-notice">{{ $t('menuImport.source.aiNotice') }}</p>
    </template>
    <!-- v-show: the note keeps its focus (and the phone keyboard) whatever the tray does. -->
    <V2MenuCaptureTray
      v-show="showTray"
      :tiles="tiles"
      :phone="phone"
      @retake="retake"
      @use-anyway="useAnyway"
      @remove="removeTile"
      @retry="retryTile"
      @add="addFiles"
    >
      <template #footer>
        <PosUiField :label="$t('menuImport.note.label')" :input-id="noteId">
          <PosUiInput
            :id="noteId"
            :value="note"
            :maxlength="NOTE_MAX"
            :placeholder="$t('menuImport.note.placeholder')"
            autocomplete="off"
            @input="note = $event"
          />
        </PosUiField>
      </template>
    </V2MenuCaptureTray>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-source-step {
    @apply tw-mx-auto tw-flex tw-w-full tw-max-w-[54rem] tw-flex-col tw-gap-4;
    .step-head {
      @apply tw-flex tw-flex-col tw-gap-1;
    }
    .step-title {
      @apply tw-m-0 tw-text-xl tw-font-bold tw-text-neutral-900 md:tw-text-2xl;
    }
    .step-subtitle {
      @apply tw-m-0 tw-text-sm tw-text-neutral-500 md:tw-text-base;
    }
    .ai-off {
      @apply tw-m-0 tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-700;
      .notice-icon {
        @apply tw-mt-0.5 tw-shrink-0 tw-text-base tw-text-neutral-500;
      }
    }
    .other-ways {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-x-4;
    }
    .other-label {
      @apply tw-text-sm tw-text-neutral-500;
    }
    .way-link {
      @apply tw-min-h-11 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-px-0 tw-text-left tw-text-sm tw-font-medium tw-text-neutral-900 tw-underline tw-underline-offset-4;
      &.is-quiet {
        @apply tw-font-normal tw-text-neutral-500;
      }
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-400 tw-no-underline;
      }
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .desktop-tip {
      @apply tw-m-0 tw-text-sm tw-text-neutral-500;
    }
    .tips {
      @apply tw-m-0 tw-grid tw-list-none tw-gap-2 tw-p-0;
      grid-template-columns: repeat(auto-fit, minmax(min(14rem, 100%), 1fr));
    }
    .tip {
      @apply tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-700;
      .tip-icon {
        @apply tw-shrink-0 tw-text-base tw-text-neutral-500;
      }
      // The update's key tip: its own row, info tint.
      &.is-key {
        @apply tw-border-sky-200 tw-bg-sky-50 tw-text-sky-700;
        grid-column: 1 / -1;
        .tip-icon {
          @apply tw-text-sky-700;
        }
      }
    }
    .ai-notice {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
  }
</style>
